import { createFileRoute } from "@tanstack/react-router";
import { GameHub } from "@/components/GameHub";
import { getGame, GAMES } from "@/data/games";

export const Route = createFileRoute("/games/$slug/")({
  head: ({ params }) => {
    const g = getGame(params.slug);
    if (!g) return { meta: [{ title: "Game not found — Mr. B Quotes" }] };
    const SITE = "https://mrbquotes.com";
    const url = `${SITE}/games/${g.slug}`;
    // Per-game share image (unique palette + copy) as a real PNG so X and
    // WhatsApp link previews render reliably. Static file in /public/share/.
    const image = `${SITE}/share/${g.slug}.png`;
    const logo = `${SITE}/favicon.png`;
    return {
      meta: [
        { title: g.seoTitle },
        { name: "description", content: g.seoDescription },
        { property: "og:title", content: g.seoTitle },
        { property: "og:description", content: g.seoDescription },
        { property: "og:url", content: url },
        { property: "og:type", content: "website" },
        { property: "og:image", content: image },
        { property: "og:image:type", content: "image/png" },
        { property: "og:image:width", content: "1200" },
        { property: "og:image:height", content: "630" },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: g.seoTitle },
        { name: "twitter:description", content: g.seoDescription },
        { name: "twitter:image", content: image },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "VideoGame",
            name: g.name,
            description: g.seoDescription,
            genre: g.type === "trivia" ? "Trivia" : "Puzzle",
            gamePlatform: "Web Browser",
            applicationCategory: "GameApplication",
            operatingSystem: "Any",
            image,
            url,
            inLanguage: "en",
            isAccessibleForFree: true,
            offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
            author: { "@type": "Organization", name: "Mr. B Quotes", url: SITE },
            publisher: {
              "@type": "Organization",
              name: "Mr. B Quotes",
              url: SITE,
              logo: { "@type": "ImageObject", url: logo },
              sameAs: [
                "https://www.youtube.com/@ministerjeb",
                "https://www.facebook.com/MrBquotes",
                "https://x.com/MrBquotes",
              ],
            },
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: SITE },
              { "@type": "ListItem", position: 2, name: "Games", item: `${SITE}/games` },
              { "@type": "ListItem", position: 3, name: g.shortName ?? g.name, item: url },
            ],
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: g.faq.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          }),
        },
      ],
    };
  },

  component: HubRoute,
  notFoundComponent: () => (
    <div className="flex min-h-screen items-center justify-center">
      <div className="text-center">
        <h1 className="font-display text-3xl text-gold">No such game</h1>
        <ul className="mt-4 flex flex-wrap justify-center gap-3">
          {GAMES.map((g) => (
            <li key={g.slug}>
              <a href={`/games/${g.slug}`} className="rounded-full border border-border px-4 py-2 text-sm hover:border-gold">{g.shortName}</a>
            </li>
          ))}
        </ul>
      </div>
    </div>
  ),
});

function HubRoute() {
  const { slug } = Route.useParams();
  const game = getGame(slug)!;
  return <GameHub game={game} />;
}
